import { clientStorage } from "@/lib/clientStorage";
import { deviceStorage } from "@/lib/deviceStorage";
import { isLacwLocalTestMode, lacwLocalTestStore } from "@/lib/lacwLocalTestStore";

const STORAGE_KEY = "cls_session_drafts_v1";
const ADMIN_BILLING_STORAGE_KEY = "cls_admin_billing_drafts_v1";

function isAdminBillingDraft(formType, brand) {
  return formType === "billing_sheet" && brand === "admin";
}

function parseDrafts(raw) {
  try {
    const value = JSON.parse(raw || "[]");
    return Array.isArray(value) ? value : [];
  } catch {
    return [];
  }
}

function read() {
  return parseDrafts(clientStorage.getItem(STORAGE_KEY));
}

function write(drafts) {
  clientStorage.setItem(STORAGE_KEY, JSON.stringify(drafts));
}

function readAdminBillingDrafts() {
  const saved = parseDrafts(deviceStorage.getItem(ADMIN_BILLING_STORAGE_KEY));
  const legacy = read().filter((draft) => isAdminBillingDraft(draft.form_type, draft.brand));
  if (legacy.length === 0) return saved;

  const merged = new Map(saved.map((draft) => [draft.id, draft]));
  legacy.forEach((draft) => {
    const current = merged.get(draft.id);
    if (!current || String(draft.updated_at) > String(current.updated_at)) merged.set(draft.id, draft);
  });
  const migrated = Array.from(merged.values());
  deviceStorage.setItem(ADMIN_BILLING_STORAGE_KEY, JSON.stringify(migrated));
  write(read().filter((draft) => !isAdminBillingDraft(draft.form_type, draft.brand)));
  return migrated;
}

function writeAdminBillingDrafts(drafts) {
  deviceStorage.setItem(ADMIN_BILLING_STORAGE_KEY, JSON.stringify(drafts));
}

export function listSessionDrafts(formType, brand) {
  if (brand === "lacw" && isLacwLocalTestMode()) return lacwLocalTestStore.listDrafts(formType);
  const drafts = isAdminBillingDraft(formType, brand) ? readAdminBillingDrafts() : read();
  return drafts
    .filter((draft) => draft.form_type === formType && draft.brand === brand)
    .sort((a, b) => String(b.updated_at).localeCompare(String(a.updated_at)));
}

export function getSessionDraft(id) {
  if (isLacwLocalTestMode()) {
    const localDraft = lacwLocalTestStore.getDraft(id);
    if (localDraft) return localDraft;
  }
  return [...readAdminBillingDrafts(), ...read()].find((draft) => draft.id === id) || null;
}

export function saveSessionDraft({ id, formType, draftName, savedBy, savedByEmail, brand, formData, matter_id }) {
  if (brand === "lacw" && isLacwLocalTestMode()) {
    return lacwLocalTestStore.saveDraft({ id, formType, draftName, savedBy, savedByEmail, formData, matter_id });
  }
  const deviceLocal = isAdminBillingDraft(formType, brand);
  const drafts = deviceLocal ? readAdminBillingDrafts() : read();
  const draftId = id || `draft_${crypto.randomUUID()}`;
  const existing = drafts.find((draft) => draft.id === draftId);
  const now = new Date().toISOString();
  const saved = {
    id: draftId,
    form_type: formType,
    draft_name: draftName,
    saved_by: savedBy,
    saved_by_email: savedByEmail,
    brand,
    form_data: formData,
    matter_id: matter_id || null,
    created_at: existing?.created_at || now,
    updated_at: now,
  };
  const next = drafts.filter((draft) => draft.id !== draftId);
  next.unshift(saved);
  if (deviceLocal) writeAdminBillingDrafts(next);
  else write(next);
  return saved;
}

export function deleteSessionDraft(id, formType, brand) {
  if (brand === "lacw" && isLacwLocalTestMode()) {
    lacwLocalTestStore.deleteDraft(id);
    return;
  }
  if (isAdminBillingDraft(formType, brand)) {
    writeAdminBillingDrafts(readAdminBillingDrafts().filter((draft) => draft.id !== id));
    return;
  }
  write(read().filter((draft) => draft.id !== id));
}
